import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Collapse from '@mui/material/Collapse';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessOutlined from '@mui/icons-material/ExpandLessOutlined';
import IconButton from '@mui/material/IconButton';

export default function LisapalveluSection({
  extras,
  upcomingExtras,
  selectedExtras,
  onToggleExtra,
  defaultOpen = false,
  hint = '',
}) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(defaultOpen);
  React.useEffect(() => { if (defaultOpen) setOpen(true); }, [defaultOpen]);

  const due = extras || [];
  const upcoming = upcomingExtras || [];
  const total = due.length + upcoming.length;

  if (total === 0) return null;

  const renderExtra = (extra, showDueKm) => {
    const title = extra.title || extra.description || extra.interval_label || extra.service_text;
    // A named extra ("Moottoriöljy", "Etujarrupalat") has its name as both title and
    // interval wording; the second line then says what it contains instead.
    let detail = showDueKm && extra.due_label ? extra.due_label : extra.interval_label || extra.service_text;
    if (detail === title) detail = extra.description !== title ? extra.description : '';
    return (
    <FormControlLabel
      key={extra.service_id}
      control={
        <Checkbox
          checked={selectedExtras.includes(extra.service_id)}
          onChange={() => onToggleExtra(extra.service_id)}
          size="small"
          sx={{ '&.Mui-checked': { color: 'secondary.main' } }}
        />
      }
      label={
        <Box>
          <Typography sx={{ fontSize: 13 }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
            {[
              detail,
              extra.service_time ? `${extra.service_time.toFixed(1)}h` : '',
              extra.part_count > 0 ? `${extra.part_count} ${t('oem.parts')}` : '',
            ].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
      }
      sx={{ mx: 0, '& .MuiFormControlLabel-label': { ml: 0.5 } }}
    />
    );
  };

  return (
    <Box sx={{ mt: 1, border: 1, borderColor: 'divider', borderRadius: '12px', overflow: 'hidden' }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: '10px 16px',
          cursor: 'pointer',
          bgcolor: '#f8fafc',
          '&:hover': { bgcolor: '#f1f5f9' },
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
          {t('oem.extras')} ({total})
        </Typography>
        <IconButton size="small" sx={{ p: 0 }}>
          {open ? <ExpandLessOutlined sx={{ fontSize: 20 }} /> : <ExpandMoreOutlined sx={{ fontSize: 20 }} />}
        </IconButton>
      </Box>

      <Collapse in={open}>
        <Box sx={{ p: '8px 16px 12px', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {hint && (
            <Typography sx={{ fontSize: 12, color: '#b45309', mb: 0.75 }}>
              {hint}
            </Typography>
          )}
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mb: 0.5 }}>
            {t('oem.extrasHelp')}
          </Typography>

          {due.map((extra) => renderExtra(extra, false))}

          {/* Not due at this service, but coming up — kept separate so a timing belt
              at 180 000 km stops carrying the same weight as a filter due now. */}
          {upcoming.length > 0 && (
            <>
              <Typography
                sx={{
                  fontSize: 11,
                  color: 'text.secondary',
                  mt: due.length > 0 ? 1 : 0,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontWeight: 600,
                }}
              >
                {t('oem.extrasUpcoming')}
              </Typography>
              {upcoming.map((extra) => renderExtra(extra, true))}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
